import { createRouter, createWebHistory } from 'vue-router'
import { DEFAULT_HERO_TAB, isValidHeroTab } from '../views/heroTabs/config'

// Every view is its own chunk, loaded when its route is first entered. The
// Vue CLI prefetch plugin is disabled in vue.config.js so these chunks are not
// downloaded ahead of time on unrelated routes.
const index = () => import(/* webpackChunkName: "view-home" */ '../views/index.vue')
const matches = () => import(/* webpackChunkName: "view-matches" */ '../views/matches.vue')
const player = () => import(/* webpackChunkName: "view-player" */ '../views/player.vue')
const hero = () => import(/* webpackChunkName: "view-hero" */ '../views/hero.vue')
const leaderboard = () => import(/* webpackChunkName: "view-leaderboard" */ '../views/leaderboard.vue')
const heros = () => import(/* webpackChunkName: "view-heros" */ '../views/heros.vue')
const items = () => import(/* webpackChunkName: "view-items" */ '../views/items.vue')
const PageNotFound = () => import(/* webpackChunkName: "view-404" */ '../views/404Page.vue')

const routes = [
  {
    path: '/',
    name: 'Home',
    component: index,
    meta: {
      title: 'KoPStats - Kingdom of Pixels Stats',
      description:
        'Search Kingdom of Pixels stats, hero win rates, item performance, match history, player rankings, and leaderboards.',
    },
  },

  {
    path: '/matches',
    name: 'matches',
    component: matches,
    meta: {
      title: 'Kingdom of Pixels Match History - KoPStats',
      description: 'Browse recent Kingdom of Pixels matches with player heroes, KDA, items, talents, MMR changes, and results.',
    },
  },

  {
    path: '/heros',
    name: 'Heros',
    component: heros,
    meta: {
      title: 'Kingdom of Pixels Hero Stats - KoPStats',
      description: 'Compare Kingdom of Pixels heroes by games played, win rate, pick rate, ban rate, KDA, and performance stats.',
    },
  },

  {
    path: '/items',
    name: 'Items',
    component: items,
    meta: {
      title: 'Kingdom of Pixels Item Stats - KoPStats',
      description: 'Compare Kingdom of Pixels item win rates, games played, scores, KDA, and average fancy points.',
    },
  },

  {
    path: '/item/:name/:tab?',
    name: 'Item',
    component: () => import(/* webpackChunkName: "view-item" */ '../views/item.vue'),
    beforeEnter: (to) => {
      if (!['heroes', 'matches'].includes(to.params.tab)) {
        return { name: 'Item', params: { name: to.params.name, tab: 'heroes' }, query: to.query, replace: true }
      }
      return true
    },
    meta: {
      title: (to) => `${to.params.name} - Kingdom of Pixels Item Stats | KoPStats`,
      description: 'Item win rates by hero, best against, and recent matches.',
    },
  },

  {
    path: '/leaderboard',
    name: 'Leaderboard',
    component: leaderboard,
    meta: {
      title: 'Kingdom of Pixels Leaderboard - KoPStats',
      description: 'View Kingdom of Pixels ranked leaderboard with player MMR, wins, losses, medals, and most played heroes.',
    },
  },

  {
    path: '/player/:name',
    name: 'Player',
    component: player,
    meta: {
      title: 'Kingdom of Pixels Player Stats - KoPStats',
      description: 'View Kingdom of Pixels player stats, rank, MMR, win rate, most played heroes, and recent match history.',
    },
  },

  {
    path: '/hero/:name/:tab?',
    name: 'Hero',
    component: hero,
    beforeEnter: (to) => {
      const tab = Array.isArray(to.params.tab) ? to.params.tab[0] : to.params.tab

      if (!tab || !isValidHeroTab(tab)) {
        return {
          name: 'Hero',
          params: { name: to.params.name, tab: DEFAULT_HERO_TAB },
          query: to.query,
          hash: to.hash,
          replace: true,
        }
      }
      return true
    },
    meta: {
      title: 'Kingdom of Pixels Hero Details - KoPStats',
      description: 'View Kingdom of Pixels hero details with win rate, best with, best against, item stats, and performance data.',
    },
  },

  {
    path: '/:pathMatch(.*)*',
    component: PageNotFound,
    meta: {
      title: 'Page Not Found - KoPStats',
      description: 'KoPStats page not found. Search Kingdom of Pixels stats, heroes, items, matches, and leaderboards.',
    },
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
})

const defaultTitle = 'KoPStats - Kingdom of Pixels Stats'
const defaultDescription =
  'KoPStats and KoP Buff provide searchable Kingdom of Pixels stats, hero win rates, item performance, match history, player rankings, and leaderboard data.'

const setMetaContent = (selector, content) => {
  const element = document.querySelector(selector)
  if (element) {
    element.setAttribute('content', content)
  }
}

router.afterEach((to) => {
  const title =
    typeof to.meta.title === 'function'
      ? to.meta.title(to)
      : to.name === 'Player' && to.params.name
        ? `${to.params.name} - Kingdom of Pixels Player Stats | KoPStats`
        : to.name === 'Hero' && to.params.name
          ? `${to.params.name} - Kingdom of Pixels Hero Stats | KoPStats`
          : to.meta.title || defaultTitle

  const description = to.meta.description || defaultDescription

  document.title = title
  setMetaContent('meta[name="description"]', description)
  setMetaContent('meta[property="og:title"]', title)
  setMetaContent('meta[property="og:description"]', description)
  setMetaContent('meta[name="twitter:title"]', title)
  setMetaContent('meta[name="twitter:description"]', description)
})

export default router
